interface CacheConfig {
  /** Default expiry time in minutes */
  defaultExpiryMinutes?: number;
  /** Whether to clear previous data when key changes */
  clearOnKeyChange?: boolean;
}

interface CacheItem<T> {
  data: T;
  timestamp: number;
  key?: string;
  expiryMinutes?: number;
}

export class CacheManager {
  private static instance: CacheManager;
  private cache: Map<string, CacheItem<any>> = new Map();
  private cleanupInterval: NodeJS.Timeout | null = null;

  private config: CacheConfig = {
    defaultExpiryMinutes: 30,
    clearOnKeyChange: true
  };

  private constructor() {
    // Start cleanup interval
    this.startCleanupInterval();
  }

  public static getInstance(): CacheManager {
    if (!CacheManager.instance) {
      CacheManager.instance = new CacheManager();
    }
    return CacheManager.instance;
  }

  public configure(config: Partial<CacheConfig>): void {
    this.config = { ...this.config, ...config };
  }

  public async getCachedData<T>(
    cacheId: string,
    fetchMethod: () => Promise<T>,
    options?: {
      key?: string;
      expiryMinutes?: number;
      clearPrevious?: boolean;
    }
  ): Promise<T> {
    const {
      key,
      expiryMinutes = this.config.defaultExpiryMinutes,
      clearPrevious = this.config.clearOnKeyChange
    } = options || {};

    // Clear previous data if key changed and clearPrevious is true
    if (key && clearPrevious) {
      const existingItem = this.cache.get(cacheId);
      if (existingItem && existingItem.key !== key) {
        this.cache.delete(cacheId);
      }
    }

    // Check if valid cached data exists
    const cachedItem = this.cache.get(cacheId);
    if (cachedItem) {
      const isExpired = this.isItemExpired(cachedItem);
      const isSameKey = !key || cachedItem.key === key;

      if (!isExpired && isSameKey) {
        return cachedItem.data;
      }
    }

    // Fetch fresh data
    try {
      const data = await fetchMethod();
      this.cache.set(cacheId, {
        data,
        timestamp: Date.now(),
        key,
        expiryMinutes
      });
      return data;
    } catch (error) {
      // If fetch fails and we have cached data, return it even if expired
      if (cachedItem) {
        console.warn(`Fetch failed, returning stale data for ${cacheId}:`, error);
        return cachedItem.data;
      }
      throw error;
    }
  }

  public clearCache(cacheId?: string): void {
    if (cacheId) {
      this.cache.delete(cacheId);
    } else {
      this.cache.clear();
    }
  }

  public clearByKey(key: string): void {
    for (const [cacheId, item] of this.cache.entries()) {
      if (item.key === key) {
        this.cache.delete(cacheId);
      }
    }
  }

  private isItemExpired(item: CacheItem<any>): boolean {
    const expiryTime = item.expiryMinutes || this.config.defaultExpiryMinutes;
    if (!expiryTime) return false;

    const expiryMs = expiryTime * 60 * 1000;
    return Date.now() - item.timestamp > expiryMs;
  }

  private startCleanupInterval(): void {
    // Run cleanup every minute
    this.cleanupInterval = setInterval(() => {
      for (const [cacheId, item] of Array.from(this.cache.entries())) {
        if (this.isItemExpired(item)) {
          this.cache.delete(cacheId);
        }
      }
    }, 60 * 1000);
  }

  public destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    this.cache.clear();
  }
}


export const cacheManager = CacheManager.getInstance();
